import { StateCreator } from 'zustand';

import { ImportFileError, InvalidProjectError, MissingRoomError } from '@/common/errors';
import { getCurrentTimestamp, getSafeTimestamp, getRandomStringSimple, simpleClone } from '@/common/utils';

import { BuildSlice } from '@/types/stores/build';
import { PlanSlice } from '@/types/stores/plan';
import { PlannerSlice } from '@/types/stores/planner';
import { ProjectSlice } from '@/types/stores/project';
import { RootActions } from '@/types/stores/rootActions';
import { Corner, ProjectId, Room, RoomId, RoomRelations } from '@/api/airchef/Api';
import { Project, ProjectJSON } from '@/types/helpers';
import { convertCmToMeasurement, convertMeasurementToCm } from '@/common/planningUtils';
import { diff } from 'datum-diff';
import { PlannerCorner, PlannerRoom } from '@/types/planner';

import { updateProjectModified } from './project/helpers';
import { migrateImportedJson } from './project/migration/migrate';

export const createRootActions: StateCreator<
  BuildSlice & PlanSlice & PlannerSlice & ProjectSlice & RootActions,
  [['zustand/devtools', never], ['zustand/immer', never]],
  [],
  RootActions
> = (set, get) => ({
  openRoom: async (projectId, roomId) => {
    const { data: projectData } = get().project;

    const room = validateRoomExistence(projectData, projectId, roomId);

    const { clearPlanner } = get();
    clearPlanner();

    const drawableRoom = convertRoomForDrawing(room);

    set(
      (state) => {
        state.build.session = state.project.session;
        state.build.corners = drawableRoom.corners;
        state.build.walls = drawableRoom.walls;
        state.build.sectors = drawableRoom.sectors;

        state.planner.currentRoom = roomId;
        state.plan.dirty = false;
      },
      undefined,
      { type: 'openRoom', projectId, roomId },
    );
  },
  saveRoom: async (projectId, roomId, close = false) => {
    const { data: projectData, session: projectSession } = get().project;
    const { session: buildSession, corners, walls, sectors } = get().build;
    if (buildSession !== projectSession) {
      // Make sure we are not saving data from existing editor session to newly created or imported project
      const { clearPlanner } = get();
      clearPlanner();
      return;
    }

    const targetRoom = validateRoomExistence(projectData, projectId, roomId);

    const roomData = convertRoomForStore(
      {
        corners,
        walls,
        sectors,
      },
      targetRoom.relations,
    );

    const room = projectData.rooms[roomId];
    if (!room) {
      throw new MissingRoomError(`No room found with id ${roomId}`);
    }

    const cornerDiff = diff(room.relations.corners, roomData.corners);
    const wallDiff = diff(room.relations.walls, roomData.walls);
    const sectorDiff = diff(room.relations.sectors, roomData.sectors);

    if (cornerDiff || wallDiff || sectorDiff || get().plan.dirty) {
      // Only update data if there are actual differences
      set(
        (state) => {
          const targetRoom = state.project.data.rooms[roomId];
          if (!targetRoom) {
            // This really should not happen
            throw new MissingRoomError('No room found after first passing check.');
          }
          const now = getCurrentTimestamp();
          targetRoom.relations = roomData;
          targetRoom.airchefValues.datesAndAudit.modifiedAt = now;
          targetRoom.airchefValues.datesAndAudit.modifiedBy = state.planner.user.data?.userPrincipalName ?? '';

          updateProjectModified(state);

          state.plan.dirty = false;

          // If the physical layout has changed, mark the room as edited by hand
          if (cornerDiff || wallDiff || sectorDiff) {
            targetRoom.airchefValues.autogenerated = false;
          }
        },
        undefined,
        { type: close ? 'closeRoom' : 'saveRoom', projectId, roomId },
      );
    }
    if (close) {
      const { clearPlanner } = get();
      clearPlanner();
    }
  },
  deleteRoom: (roomId) => {
    const { project } = get();
    project.deleteRoom(roomId);
  },
  clearPlanner: () => {
    const { build, plan, planner } = get();

    build.clear();
    plan.clear();
    planner.clear();
  },
  exportProject: () => {
    const { data: projectData } = get().project;
    const data: ProjectJSON = {
      generator: 'Halton AirChef',
      date: Temporal.Now.zonedDateTimeISO().toString(),
      project: projectData,
    };

    const fileName = `${projectData.airchefValues.name.replace(/\s/g, '_') || 'Unnamed_project'}-${getSafeTimestamp()}.json`;

    const file = new File([JSON.stringify(data)], fileName, {
      type: 'application/json',
    });

    const link = window.document.createElement('a');
    link.href = URL.createObjectURL(file);
    link.style.height = '0px';
    link.style.width = '0px';
    link.style.display = 'block';
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    setTimeout(() => {
      document.body.removeChild(link);
    }, 0);
  },
  async *importProjectLocal(jsonData, persist, force, doMigrate) {
    const templates = get().planner.projectTemplates.map;
    const firstTemplate = Object.values(templates)[0];

    const gen = migrateImportedJson(jsonData, firstTemplate?.schemaPath, doMigrate, force);
    let data: null | ProjectJSON = null;
    let done = false;
    do {
      const result = await gen.next();
      done = result.done ?? false;
      switch (result.value.kind) {
        case 'done':
        case 'todo':
          yield result.value;
          break;
        case 'project':
          data = result.value.project;
          break;
      }
    } while (!done);

    if (!data) throw new ImportFileError('Could not get migrated project data');

    console.log('setting migrated project to state');
    set(
      (state) => {
        state.project.importing = true;
        state.project.session = getRandomStringSimple(6);
        state.project.data = data.project;
      },
      undefined,
      { type: 'importProject', importing: true },
    );

    const { data: projectData } = get().project;
    await persist(projectData);
    set(
      (state) => {
        state.project.importing = false;
      },
      undefined,
      { type: 'importProject', importing: false },
    );

    return {
      id: get().project.data.id,
      kind: 'project',
    };
  },
});

function validateRoomExistence(projectData: Project, projectId: ProjectId, roomId: RoomId) {
  if (projectData.id !== projectId) {
    throw new InvalidProjectError('No project with given id');
  }

  const room = projectData.rooms[roomId];

  if (!room) {
    throw new MissingRoomError('No room with the given id in the current project');
  }

  return room;
}

function convertRoomForDrawing(sourceRoom: Room): PlannerRoom {
  const source = simpleClone(sourceRoom);
  const corners: Record<string, PlannerCorner> = {};

  Object.values(source.relations.corners).forEach((corner) => {
    const p: PlannerCorner = {
      id: corner.id,
      x: convertMeasurementToCm(corner.position.x),
      y: convertMeasurementToCm(corner.position.y),
      z: convertMeasurementToCm(corner.position.z),
      height: convertMeasurementToCm(corner.height),
    };

    corners[p.id] = p;
  });

  return {
    corners,
    walls: source.relations.walls,
    sectors: source.relations.sectors,
  };
}

function convertRoomForStore(sourceRoom: PlannerRoom, targetRelations: Readonly<RoomRelations>): RoomRelations {
  const source = simpleClone(sourceRoom);
  const corners: Record<string, Corner> = {};

  Object.values(source.corners).forEach((pc) => {
    const c: Corner = {
      id: pc.id,
      position: {
        x: convertCmToMeasurement(pc.x),
        y: convertCmToMeasurement(pc.y),
        z: convertCmToMeasurement(pc.z),
      },
      height: convertCmToMeasurement(pc.height),
    };

    corners[c.id] = c;
  });

  return {
    ...targetRelations,
    walls: source.walls,
    sectors: source.sectors,
    corners,
  };
}
